import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAllSubscriptions,
  getPaymentRecords,
  approvePayment,
  rejectPayment,
  updateSubscriptionAccess,
  getAccessControlLogs,
  UserSubscription,
  PaymentRecord,
  AccessControlLog
} from '../../services/supabaseService';
import { 
  Shield, 
  DollarSign, 
  Users, 
  Clock, 
  CheckCircle, 
  XCircle,
  Lock,
  Unlock,
  AlertCircle,
  TrendingUp
} from 'lucide-react';

const AccessControlDashboard = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const [subscriptions, setSubscriptions] = useState<UserSubscription[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [logs, setLogs] = useState<AccessControlLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  useEffect(() => {
    if (user && userProfile?.role === 'super_admin') {
      loadDashboardData();
    }
  }, [user, userProfile]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [subsData, paymentsData, logsData] = await Promise.all([
        getAllSubscriptions(),
        getPaymentRecords(),
        getAccessControlLogs()
      ]);

      setSubscriptions(subsData);
      setPayments(paymentsData);
      setLogs(logsData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePayment = async (paymentId: string) => {
    if (!user) return;
    
    try {
      await approvePayment(paymentId, user.id);
      await loadDashboardData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRejectPayment = async () => {
    if (!user || !selectedPayment || !rejectionReason) return;
    
    try {
      await rejectPayment(selectedPayment, user.id, rejectionReason);
      setShowRejectModal(false);
      setSelectedPayment(null);
      setRejectionReason('');
      await loadDashboardData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateAccess = async (subscriptionId: string, status: 'active' | 'locked' | 'grace_period') => {
    try {
      await updateSubscriptionAccess(subscriptionId, status);
      await loadDashboardData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const activeSubscriptions = subscriptions.filter(s => s.access_status === 'active').length;
  const lockedSubscriptions = subscriptions.filter(s => s.access_status === 'locked').length;
  const pendingPayments = payments.filter(p => p.payment_status === 'pending').length;
  const totalRevenue = payments
    .filter(p => p.payment_status === 'approved')
    .reduce((sum, p) => sum + p.amount_vatu, 0);

  return (
    <>
      <Helmet>
        <title>Access Control Dashboard - communitySaving</title>
        <meta name="description" content="Monitor subscriptions, approve payments, and manage user access for communitySaving" />
      </Helmet>

      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Shield className="h-8 w-8 text-primary" />
              Access Control Dashboard
            </h1>
            <p className="text-muted-foreground mt-2">
              Monitor subscriptions, manage payments, and control user access
            </p>
          </div>

          {error && (
            <div className="mb-6 bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-card rounded-lg shadow-elevation-1 p-6 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Subscriptions</p>
                  <p className="text-3xl font-bold text-foreground mt-2">{activeSubscriptions}</p>
                </div>
                <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-primary" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-elevation-1 p-6 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Locked Access</p>
                  <p className="text-3xl font-bold text-destructive mt-2">{lockedSubscriptions}</p>
                </div>
                <div className="h-12 w-12 bg-destructive/10 rounded-lg flex items-center justify-center">
                  <Lock className="h-6 w-6 text-destructive" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-elevation-1 p-6 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Payments</p>
                  <p className="text-3xl font-bold text-warning mt-2">{pendingPayments}</p>
                </div>
                <div className="h-12 w-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Clock className="h-6 w-6 text-warning" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg shadow-elevation-1 p-6 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Revenue (VT)</p>
                  <p className="text-3xl font-bold text-success mt-2">{totalRevenue.toLocaleString()}</p>
                </div>
                <div className="h-12 w-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-success" />
                </div>
              </div>
            </div>
          </div>

          {/* Pending Payments Section */}
          <div className="bg-card rounded-lg shadow-elevation-1 p-6 border border-border mb-8">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-warning" />
              Pending Payments
            </h2>

            {payments.filter(p => p.payment_status === 'pending').length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No pending payments</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">User</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Amount (VT)</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Reference</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments
                      .filter(p => p.payment_status === 'pending')
                      .map((payment) => (
                        <tr key={payment.id} className="border-b border-border hover:bg-accent/5">
                          <td className="py-3 px-4 text-sm text-foreground">
                            {payment.user?.full_name || payment.user?.email || 'Unknown'}
                          </td>
                          <td className="py-3 px-4 text-sm text-foreground font-medium">
                            {payment.amount_vatu.toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {payment.payment_reference || 'N/A'}
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {new Date(payment.payment_date).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApprovePayment(payment.id)}
                                className="px-3 py-1 bg-success text-white rounded-md hover:bg-success/90 transition-colors text-sm flex items-center gap-1"
                              >
                                <CheckCircle className="h-4 w-4" />
                                Approve
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedPayment(payment.id);
                                  setShowRejectModal(true);
                                }}
                                className="px-3 py-1 bg-destructive text-white rounded-md hover:bg-destructive/90 transition-colors text-sm flex items-center gap-1"
                              >
                                <XCircle className="h-4 w-4" />
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Subscriptions Overview */}
          <div className="bg-card rounded-lg shadow-elevation-1 p-6 border border-border mb-8">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              User Subscriptions
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">User</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Plan</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Expires</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((subscription) => (
                    <tr key={subscription.id} className="border-b border-border hover:bg-accent/5">
                      <td className="py-3 px-4 text-sm text-foreground">
                        {subscription.user?.full_name || subscription.user?.email || 'Unknown'}
                      </td>
                      <td className="py-3 px-4 text-sm text-foreground">
                        {subscription.plan?.name || 'Unknown Plan'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          subscription.access_status === 'active' ?'bg-success/10 text-success' 
                            : subscription.access_status === 'locked' ?'bg-destructive/10 text-destructive' :'bg-warning/10 text-warning'
                        }`}>
                          {subscription.access_status === 'active' ? (
                            <Unlock className="h-3 w-3" />
                          ) : (
                            <Lock className="h-3 w-3" />
                          )}
                          {subscription.access_status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {new Date(subscription.end_date).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        {subscription.access_status === 'locked' ? (
                          <button
                            onClick={() => handleUpdateAccess(subscription.id, 'active')}
                            className="px-3 py-1 bg-success text-white rounded-md hover:bg-success/90 transition-colors text-sm flex items-center gap-1"
                          >
                            <Unlock className="h-4 w-4" />
                            Unlock
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUpdateAccess(subscription.id, 'locked')}
                            className="px-3 py-1 bg-destructive text-white rounded-md hover:bg-destructive/90 transition-colors text-sm flex items-center gap-1"
                          >
                            <Lock className="h-4 w-4" />
                            Lock
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Activity Log */}
          <div className="bg-card rounded-lg shadow-elevation-1 p-6 border border-border">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5 text-muted-foreground" />
              Recent Activity
            </h2>

            <div className="space-y-3">
              {logs.slice(0, 10).map((log) => (
                <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg bg-accent/5 border border-border">
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Shield className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground">
                      <span className="font-medium">{log.user?.full_name || log.user?.email}</span>
                      {' - '}
                      <span className="text-muted-foreground">{log.action.replace(/_/g, ' ')}</span>
                    </p>
                    {log.reason && (
                      <p className="text-xs text-muted-foreground mt-1">{log.reason}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(log.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Reject Payment Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-card rounded-lg shadow-elevation-3 p-6 max-w-md w-full">
              <h3 className="text-lg font-bold text-foreground mb-4">Reject Payment</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Please provide a reason for rejecting this payment:
              </p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                rows={4}
                placeholder="Enter rejection reason..."
              />
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleRejectPayment}
                  disabled={!rejectionReason.trim()}
                  className="flex-1 px-4 py-2 bg-destructive text-white rounded-md hover:bg-destructive/90 transition-colors disabled:opacity-50"
                >
                  Reject Payment
                </button>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedPayment(null);
                    setRejectionReason('');
                  }}
                  className="flex-1 px-4 py-2 border border-border rounded-md hover:bg-accent/5 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AccessControlDashboard;